
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { BlogPost } from "@/types/blog";

type AdminBlogPost = BlogPost;

export const useBlogModule = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [posts, setPosts] = useState<AdminBlogPost[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [tabFilter, setTabFilter] = useState("all");
  const [editingPostId, setEditingPostId] = useState<string | undefined>(undefined);
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  // Load posts from Supabase
  const loadPosts = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      // Convert the database data to match our BlogPost type
      const formattedPosts: AdminBlogPost[] = data.map(post => ({
        ...post,
        tags: post.tags ?? [],
      }));
      
      setPosts(formattedPosts);
    } catch (error) {
      console.error("Error loading posts:", error);
      toast.error("Erro ao carregar posts");
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    loadPosts();
  }, []);
  
  // Handle post editing
  const handleEditPost = (id: string) => {
    setEditingPostId(id);
  };
  
  // Handle post creation
  const handleCreatePost = () => {
    setIsCreatingPost(true);
  };
  
  // Handle cancel editing/creating
  const handleCancelEdit = () => {
    setEditingPostId(undefined);
    setIsCreatingPost(false);
    loadPosts();
  };
  
  // View post
  const handleViewPost = (post: BlogPost) => {
    // In a future implementation, this would navigate to the post page in preview mode
    toast.info(`Visualização do post "${post.title}" em desenvolvimento`);
  };

  return {
    isLoading,
    posts,
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    tabFilter,
    setTabFilter,
    editingPostId,
    setEditingPostId,
    isCreatingPost,
    setIsCreatingPost,
    isDeleting,
    setIsDeleting,
    loadPosts,
    handleEditPost,
    handleCreatePost,
    handleCancelEdit,
    handleViewPost
  };
};
